import React, { useState, useEffect, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { loadUserShoppingLists, deleteShoppingList, getLatestValidReceipt, buildBasketItemsFromReceipt } from './utils/db';
import { db } from './firebase';
import { signOutUser, upgradeGuestToGoogle, handleRedirectResult } from './utils/authHelpers';
import { trackScreen, trackListSaved } from './utils/analytics';
import { updatePresence } from './utils/db';
import './App.css';
import Home from './Home';
import BrowsePage from './pages/BrowsePage';
import CategoryPage from './pages/CategoryPage';
import BasketPage from './pages/BasketPage';
import Toast from './components/Toast';
import BottomNavigation from './components/BottomNavigation';
import PennyChat from './components/PennyChat';
import BasketNamingSheet from './components/BasketNamingSheet';
import MorePage from './pages/MorePage';
import AdminPage from './pages/AdminPage';
import FeedbackPage from './pages/FeedbackPage';
import LegalPage from './pages/LegalPage';
import AboutPage from './pages/AboutPage';
import PastScansPage from './pages/PastScansPage';
import ExplorePage from './pages/ExplorePage';
import BetaFeedbackPage from './pages/BetaFeedbackPage';

import SignInSheet from './components/SignInSheet';
import UpgradePrompt from './components/UpgradePrompt';
import ReceiptUploadSheet from './components/ReceiptUploadSheet';

// Page titles shown in the compact header
const PAGE_TITLES = {
  home:     '',
  browse:   'Browse',
  explore:  'Explore',
  category: 'Category',
  basket:   'Shopping List',
  more:     'More',
  admin:    'Admin',
  feedback: 'Feedback',
  legal:      'Privacy & Terms',
  about:      'About',
  'past-scans':     'Past scans',
  'beta-feedback':  'Your experience',
};

// Page icons shown next to the title (inline SVG, 16×16, stroke-based)
const PAGE_ICONS = {
  explore: (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="10" cy="10" r="7.5"/>
      <polygon points="10,3.5 12,10 10,11.5 8,10" fill="currentColor" stroke="none"/>
      <polygon points="10,16.5 8,10 10,11.5 12,10" fill="none" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  browse: (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="7" height="7" rx="1"/>
      <rect x="11" y="2" width="7" height="7" rx="1"/>
      <rect x="2" y="11" width="7" height="7" rx="1"/>
      <rect x="11" y="11" width="7" height="7" rx="1"/>
    </svg>
  ),
  category: (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="3" y1="5" x2="17" y2="5"/>
      <line x1="3" y1="10" x2="17" y2="10"/>
      <line x1="3" y1="15" x2="13" y2="15"/>
    </svg>
  ),
  basket: (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 2L3 6v11a2 2 0 002 2h10a2 2 0 002-2V6l-3-4z"/>
      <line x1="3" y1="6" x2="17" y2="6"/>
      <path d="M13 10a3 3 0 01-6 0"/>
    </svg>
  ),
  more: (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="10" cy="10" r="1.2" fill="currentColor" stroke="none"/>
      <circle cx="4"  cy="10" r="1.2" fill="currentColor" stroke="none"/>
      <circle cx="16" cy="10" r="1.2" fill="currentColor" stroke="none"/>
    </svg>
  ),
  admin: (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 10h2l2-7 4 14 2-7h4"/>
    </svg>
  ),
};

function App() {
  // Page navigation state
  const [currentPage,     setCurrentPage]     = useState('home');
  const [previousPage,    setPreviousPage]    = useState('home');
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Browse state persistence — keep BrowsePage mounted when going to basket
  const [browseMounted, setBrowseMounted]   = useState(false);
  const browseScrollY                       = React.useRef(0);

  // Basket state
  const [basket, setBasket]             = useState([]);
  const [savedBaskets, setSavedBaskets] = useState([]);   // locally-saved (localStorage)
  const [firestoreLists, setFirestoreLists] = useState([]); // receipt-derived (Firestore)
  const [toast, setToast]               = useState(null);

  // Aisle view memory — persists across page navigation
  const [aisleExpandedCategory, setAisleExpandedCategory] = useState(null);

  // Deep-link from home quick-start chips → browse
  const [browseDeepLink, setBrowseDeepLink] = useState(null);

  // Duplicate-family guard — holds a pending add waiting for user confirmation
  const [pendingBasketAdd, setPendingBasketAdd] = useState(null);
  // Ref so the guard always reads the current basket even when the prop is stale
  const basketRef = useRef(basket);
  useEffect(() => { basketRef.current = basket; }, [basket]);

  // Bottom nav state
  const [activeTab, setActiveTab]         = useState('home');
  const [browseNavKey, setBrowseNavKey]   = useState(0);
  const [isPennyChatOpen, setIsPennyChatOpen] = useState(false);

  // Naming sheet state
  const [showNamingSheet, setShowNamingSheet] = useState(false);

  // ── Auth state ────────────────────────────────────────────────
  const [currentUser, setCurrentUser]     = useState(null);
  const [isAuthReady, setIsAuthReady]     = useState(false);
  const [showSignIn, setShowSignIn]       = useState(false);

  // ── Upgrade prompt state ──────────────────────────────────────
  // trigger: null | 'list_saved' | 'returning' | 'usage'
  const [upgradePrompt, setUpgradePrompt] = useState(null);

  // ── Latest valid receipt — powers home screen state ───────────
  // null = not loaded yet / not found; object = valid receipt within 14 days, ≥70% match
  const [latestReceipt, setLatestReceipt] = useState(null);

  // ── Receipt scan sheet — global so it can be opened from any screen ──────────
  const [receiptSheetOpen, setReceiptSheetOpen] = useState(false);


  // One prompt per session max — use sessionStorage flag
  const maybeShowUpgrade = (trigger) => {
    if (sessionStorage.getItem('cms_upg_session')) return; // already shown one this session
    setUpgradePrompt(trigger);
    sessionStorage.setItem('cms_upg_session', '1');
    if (trigger === 'returning') {
      localStorage.setItem('cms_upg_return_shown', '1'); // don't repeat on future sessions
    }
  };

  const dismissUpgrade = () => setUpgradePrompt(null);

  // ── Handle Google redirect result on page load ───────────────
  const [authProcessing, setAuthProcessing] = useState(false);

  useEffect(() => {
    setAuthProcessing(true);
    handleRedirectResult()
      .then(result => {
        if (result?.user) {
          console.log('[Auth] Redirect complete:', result.user.email);
        } else {
          console.log('[Auth] No pending redirect result (normal load)');
        }
      })
      .catch(err => console.warn('[Auth] Redirect error:', err.code))
      .finally(() => setAuthProcessing(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auth listener + load Firestore lists once user is known ──
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setIsAuthReady(true);
      if (!user) {
        setShowSignIn(true);
      } else {
        setShowSignIn(false);
        loadUserShoppingLists().then(lists => setFirestoreLists(lists));
        // Load latest valid receipt for home screen state detection (non-blocking)
        getLatestValidReceipt(db, user.uid).then(r => setLatestReceipt(r)).catch(() => {});
      }
    });
    return unsub;
  }, []);

  // ── Session counter ───────────────────────────────────────────
  useEffect(() => {
    const count = parseInt(localStorage.getItem('cms_session_count') || '0', 10) + 1;
    localStorage.setItem('cms_session_count', count.toString());
  }, []);


  // ── Trigger: returning guest (session 2+, once ever) ─────────
  useEffect(() => {
    if (!isAuthReady || !currentUser?.isAnonymous) return;
    const sessionCount = parseInt(localStorage.getItem('cms_session_count') || '0', 10);
    const alreadyShown = localStorage.getItem('cms_upg_return_shown');
    if (sessionCount >= 2 && !alreadyShown) {
      const t = setTimeout(() => maybeShowUpgrade('returning'), 2500);
      return () => clearTimeout(t);
    }
  }, [isAuthReady, currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trigger: meaningful usage (3+ items in basket) ───────────
  useEffect(() => {
    if (!currentUser?.isAnonymous) return;
    if (basket.length >= 3) {
      maybeShowUpgrade('usage');
    }
  }, [basket.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persistence ──────────────────────────────────────────
  useEffect(() => {
    const savedBasket = localStorage.getItem('chopmyshop-basket');
    if (savedBasket) {
      try { setBasket(JSON.parse(savedBasket)); } catch (e) {}
    }
    const savedList = localStorage.getItem('chopmyshop-saved-baskets');
    if (savedList) {
      try { setSavedBaskets(JSON.parse(savedList)); } catch (e) {}
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('chopmyshop-basket', JSON.stringify(basket));
  }, [basket]);

  useEffect(() => {
    localStorage.setItem('chopmyshop-saved-baskets', JSON.stringify(savedBaskets));
  }, [savedBaskets]);

  // ── Analytics: track screen on every page change ─────────
  useEffect(() => {
    trackScreen(currentPage);
  }, [currentPage]);

  // ── Presence heartbeat ────────────────────────────────────
  // Write on mount + whenever page changes, then refresh every 60s.
  useEffect(() => {
    if (!isAuthReady || !currentUser) return;
    const uid  = currentUser.uid;
    const anon = currentUser.isAnonymous;
    updatePresence(uid, currentPage, anon);
    const interval = setInterval(() => updatePresence(uid, currentPage, anon), 60_000);
    return () => clearInterval(interval);
  }, [isAuthReady, currentUser, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scroll to top on every page / category change ────────
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage, selectedCategory]);

  // ── Basket actions ────────────────────────────────────────
  // displayName is optional — callers that know a more specific name (e.g. "Heinz Baked Beans"
  // vs the family name "Baked Beans") can pass it; all other callers omit it.

  // Internal — performs the actual basket mutation unconditionally.
  const doAddToBasket = (product, prices, preference, displayName) => {
    setBasket(prev => {
      // Match on both id and variant — mature cheddar and mild cheddar are separate list items
      const existingIndex = prev.findIndex(
        item => item.id === product.id && (item.variant || null) === (product.variant || null)
      );
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], quantity: updated[existingIndex].quantity + 1 };
        return updated;
      }
      return [...prev, { ...product, quantity: 1, prices, comparisonPreference: preference || null }];
    });
    showToast('Added to your list', displayName || product.name, {
      label: 'View list',
      fn: () => {
        browseScrollY.current = window.scrollY;
        setPreviousPage(currentPage);
        setCurrentPage('basket');
        setActiveTab('basket');
        loadUserShoppingLists().then(lists => setFirestoreLists(lists));
      },
    });
  };

  // Public — checks for same-family duplicates before adding.
  const addToBasket = (product, prices, preference, displayName) => {
    const familyConflict = product.productId
      ? basketRef.current.find(item => item.productId === product.productId && item.id !== product.id)
      : null;
    if (familyConflict) {
      setPendingBasketAdd({ product, prices, preference, displayName, conflictItem: familyConflict });
      return;
    }
    doAddToBasket(product, prices, preference, displayName);
  };

  const removeFromBasket = (productId) => {
    setBasket(basket.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    setBasket(prev => prev.map(item =>
      item.id === productId ? { ...item, quantity: newQuantity } : item
    ));
  };

  const clearBasket = () => {
    const count = basket.length;
    setBasket([]);
    localStorage.removeItem('chopmyshop-basket');
    showToast('List cleared', `${count} item${count !== 1 ? 's' : ''} removed`);
  };

  // ── Receipt-based list building ──────────────────────────────

  // State B: no basket → replace basket with receipt items and navigate to list
  const handleRecreateFromReceipt = async () => {
    if (!latestReceipt) return;
    const items = await buildBasketItemsFromReceipt(db, latestReceipt.matchedItems);
    if (items.length === 0) {
      showToast('Nothing to load', 'No items could be matched from this receipt');
      return;
    }
    setBasket(items);
    showToast('List recreated!', `${items.length} item${items.length !== 1 ? 's' : ''} from your last receipt`);
    setPreviousPage('home');
    setCurrentPage('basket');
    setActiveTab('basket');
  };

  // State D: has basket → merge receipt items in (increase qty for existing, add new)
  const handleAddFromReceipt = async () => {
    if (!latestReceipt) return;
    const items = await buildBasketItemsFromReceipt(db, latestReceipt.matchedItems);
    if (items.length === 0) {
      showToast('Nothing to add', 'No matched items found in this receipt');
      return;
    }
    setBasket(prev => {
      const merged = [...prev];
      items.forEach(item => {
        const idx = merged.findIndex(i => i.id === item.id);
        if (idx >= 0) {
          merged[idx] = { ...merged[idx], quantity: merged[idx].quantity + item.quantity };
        } else {
          merged.push(item);
        }
      });
      return merged;
    });
    showToast('Items added!', `From your last receipt`);
    setPreviousPage('home');
    setCurrentPage('basket');
    setActiveTab('basket');
  };

  // ── Save basket flow ──────────────────────────────────────
  const handleSaveBasket = () => {
    showToast('Basket saved!', `${basket.length} item${basket.length !== 1 ? 's' : ''}`);
    // Short beat, then open naming sheet
    setTimeout(() => setShowNamingSheet(true), 700);
  };

  const handleNameBasket = (name) => {
    const newSaved = {
      id:          Date.now().toString(),
      name,
      items:       JSON.parse(JSON.stringify(basket)),
      savedAt:     new Date().toISOString(),
      lastUsedAt:  new Date().toISOString(),
    };
    setSavedBaskets(prev => [newSaved, ...prev]);
    trackListSaved(basket.length);
    setShowNamingSheet(false);
    // Trigger: first list saved — nudge guest to upgrade
    if (currentUser?.isAnonymous) {
      setTimeout(() => maybeShowUpgrade('list_saved'), 800);
    }
  };

  // ── Load saved basket ─────────────────────────────────────
  const handleLoadSavedBasket = (mode, savedBasket) => {
    if (mode === 'replace') {
      setBasket(savedBasket.items.map(i => ({ ...i })));
    } else {
      setBasket(prev => {
        const merged = [...prev];
        savedBasket.items.forEach(item => {
          const idx = merged.findIndex(i => i.id === item.id);
          if (idx >= 0) {
            merged[idx] = { ...merged[idx], quantity: merged[idx].quantity + item.quantity };
          } else {
            merged.push({ ...item });
          }
        });
        return merged;
      });
    }
    setSavedBaskets(prev => prev.map(b =>
      b.id === savedBasket.id ? { ...b, lastUsedAt: new Date().toISOString() } : b
    ));
    showToast(mode === 'replace' ? 'Basket loaded!' : 'Items added!', savedBasket.name);
  };

  const handleDeleteSavedBasket = (id) => {
    // Check if this is a Firestore-backed list or a local one
    const firestoreItem = firestoreLists.find(l => l.id === id);
    if (firestoreItem) {
      deleteShoppingList(id);
      setFirestoreLists(prev => prev.filter(l => l.id !== id));
    } else {
      setSavedBaskets(prev => prev.filter(b => b.id !== id));
    }
  };

  // ── Toast ─────────────────────────────────────────────────
  const showToast = (message, productName, action) => setToast({ message, productName, action });
  const closeToast = () => setToast(null);

  // ── Navigation ────────────────────────────────────────────
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'browse') {
      setCurrentPage('browse');
      setBrowseNavKey(k => k + 1);
    } else if (tab === 'explore') {
      setCurrentPage('explore');
    } else if (tab === 'basket') {
      browseScrollY.current = window.scrollY; // save scroll before leaving browse
      setPreviousPage(currentPage);
      setCurrentPage('basket');
      loadUserShoppingLists().then(lists => setFirestoreLists(lists));
    } else if (tab === 'home') {
      setCurrentPage('home');
    } else if (tab === 'more') {
      setCurrentPage('more');
    }
  };

  const handleLogoClick = () => {
    setCurrentPage('home');
    setActiveTab('home');
  };

  // Brief overlay shown while processing a Google redirect result
  if (authProcessing) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#faf8f3', flexDirection: 'column', gap: 16 }}>
        <img src="/ChopMyShop Logo.svg" alt="ChopMyShop" style={{ height: 60 }} />
        <p style={{ fontSize: '0.9rem', color: '#666', fontFamily: 'inherit' }}>Signing you in…</p>
      </div>
    );
  }

  return (
    <div className="App">

      {/* ── Compact sticky header ── */}
      <nav className={`main-nav${currentPage === 'home' ? ' main-nav--hidden' : ''}`}>
        <img
          src="/ChopMyShop Logo.svg"
          alt="ChopMyShop — home"
          className="nav-logo"
          onClick={handleLogoClick}
        />
        {PAGE_TITLES[currentPage] && (
          <span className="nav-page-title">
            {PAGE_ICONS[currentPage] && (
              <span className="nav-page-icon">{PAGE_ICONS[currentPage]}</span>
            )}
            {PAGE_TITLES[currentPage]}
          </span>
        )}
      </nav>

      {/* ── Pages ── */}
      {currentPage === 'home' && (
        <Home
          onNavigate={(page, aisleId, subcategoryId) => {
            if (page === 'browse') setBrowseDeepLink({ aisleId: aisleId || null, subcategoryId: subcategoryId || null });
            setCurrentPage(page);
            setActiveTab(page === 'browse' ? 'browse' : page);
          }}
          basketCount={basket.reduce((sum, item) => sum + (item.quantity || 1), 0)}
          user={currentUser}
          latestReceipt={latestReceipt}
          onRecreateList={handleRecreateFromReceipt}
          onAddFromReceipt={handleAddFromReceipt}
          onScanReceipt={() => setReceiptSheetOpen(true)}
        />
      )}

      {/* BrowsePage stays mounted when navigating to basket so aisle/product state is preserved */}
      {(currentPage === 'browse' || browseMounted) && (() => {
        if (currentPage === 'browse' && !browseMounted) setBrowseMounted(true);
        return (
          <div style={{ display: currentPage === 'browse' ? 'block' : 'none' }}>
            <BrowsePage
              onNavigateToCategory={(category) => {
                setCurrentPage('category');
                setSelectedCategory(category);
              }}
              navKey={browseNavKey}
              onAddToBasket={addToBasket}
              aisleExpandedCategory={aisleExpandedCategory}
              onAisleExpandedCategoryChange={setAisleExpandedCategory}
              browseDeepLink={browseDeepLink}
              onBrowseDeepLinkConsumed={() => setBrowseDeepLink(null)}
            />
          </div>
        );
      })()}

      {currentPage === 'explore' && (
        <ExplorePage
          onAddToBasket={addToBasket}
        />
      )}

      {currentPage === 'category' && (
        <CategoryPage
          category={selectedCategory}
          onAddToBasket={addToBasket}
          onBack={() => setCurrentPage('browse')}
        />
      )}

      {currentPage === 'basket' && (
        <BasketPage
          basket={basket}
          onRemove={removeFromBasket}
          onUpdateQuantity={updateQuantity}
          onClear={clearBasket}
          onSave={handleSaveBasket}
          savedBaskets={[...firestoreLists, ...savedBaskets]}
          onLoadSavedBasket={handleLoadSavedBasket}
          onDeleteSavedBasket={handleDeleteSavedBasket}
          onNavigateToProducts={() => { setCurrentPage('explore'); setActiveTab('explore'); }}
          onOpenPenny={() => setIsPennyChatOpen(true)}
          fromPage={previousPage}
          onBack={() => {
            const browsing = ['browse', 'aisle', 'subcategory', 'category'];
            setCurrentPage(previousPage);
            setActiveTab(browsing.includes(previousPage) ? 'browse' : previousPage);
            // Restore scroll position to where user was in browse
            requestAnimationFrame(() => window.scrollTo(0, browseScrollY.current));
          }}
        />
      )}

      {currentPage === 'more' && (
        <MorePage
          onNavigate={(page) => { setPreviousPage(currentPage); setCurrentPage(page); }}
          user={currentUser}
          onSignIn={() => setShowSignIn(true)}
          onUpgrade={upgradeGuestToGoogle}
          onSignOut={signOutUser}
          onOpenScan={() => setReceiptSheetOpen(true)}
        />
      )}

      {currentPage === 'admin' && currentUser?.email === 'hello.chopmyshop@gmail.com' && (
        <AdminPage onBack={() => setCurrentPage('more')} />
      )}

      {currentPage === 'feedback' && (
        <FeedbackPage onBack={() => setCurrentPage('more')} originPage={previousPage} />
      )}
      {currentPage === 'legal' && (
        <LegalPage onBack={() => setCurrentPage('more')} />
      )}
      {currentPage === 'about' && (
        <AboutPage onBack={() => setCurrentPage('more')} />
      )}
      {currentPage === 'past-scans' && (
        <PastScansPage
          onBack={() => setCurrentPage('more')}
          onNavigate={(page) => { setCurrentPage(page); setActiveTab(page); }}
        />
      )}

      {currentPage === 'beta-feedback' && (
        <BetaFeedbackPage onBack={() => setCurrentPage(previousPage || 'more')} />
      )}

      {/* ── Duplicate-family confirmation ── */}
      {pendingBasketAdd && (() => {
        const TIER_LABELS = { basic: 'Value', standard: 'Standard', premium: 'Premium', organic: 'Organic' };
        const existingTier = TIER_LABELS[pendingBasketAdd.conflictItem.tier?.toLowerCase()] || pendingBasketAdd.conflictItem.tier || null;
        const newTier      = TIER_LABELS[pendingBasketAdd.product.tier?.toLowerCase()]      || pendingBasketAdd.product.tier      || null;
        const productName  = pendingBasketAdd.conflictItem.name;
        return (
        <>
          <div className="sheet-backdrop" onClick={() => setPendingBasketAdd(null)} />
          <div className="dup-confirm-sheet">
            <div className="sheet-handle" />
            <p className="dup-confirm-title">Already in your list</p>
            <p className="dup-confirm-body">
              You already have <strong>{productName}{existingTier ? ` (${existingTier})` : ''}</strong> in your list.
              {newTier ? ` Add the ${newTier} version too?` : ' Add this one as well?'}
            </p>
            <button
              className="dup-confirm-btn dup-confirm-btn--add"
              onClick={() => { doAddToBasket(pendingBasketAdd.product, pendingBasketAdd.prices, pendingBasketAdd.preference, pendingBasketAdd.displayName); setPendingBasketAdd(null); }}
            >
              {newTier ? `Yes, add ${newTier}` : 'Add anyway'}
            </button>
            <button
              className="dup-confirm-btn dup-confirm-btn--cancel"
              onClick={() => setPendingBasketAdd(null)}
            >
              Cancel
            </button>
          </div>
        </>
        );
      })()}

      {/* ── Toast ── */}
      {toast && (
        <Toast
          message={toast.message}
          productName={toast.productName}
          onAction={toast.action?.fn}
          actionLabel={toast.action?.label}
          onClose={closeToast}
        />
      )}

      {/* ── Bottom Navigation ── */}
      <BottomNavigation
        activeTab={activeTab}
        onTabChange={handleTabChange}
        basketCount={basket.reduce((sum, item) => sum + (item.quantity || 1), 0)}
      />

      {/* ── Penny Chat Overlay ── */}
      <PennyChat
        isOpen={isPennyChatOpen}
        onClose={() => setIsPennyChatOpen(false)}
        basket={basket}
        onNavigate={(page) => { setCurrentPage(page); setActiveTab(page); }}
      />

      {/* ── Basket Naming Sheet ── */}
      <BasketNamingSheet
        isOpen={showNamingSheet}
        onSave={handleNameBasket}
        onSkip={() => setShowNamingSheet(false)}
        basketItems={basket}
      />

      {/* ── Sign-in sheet — shown only when Firebase has no session ── */}
      {isAuthReady && showSignIn && (
        <SignInSheet onDismiss={() => setShowSignIn(false)} />
      )}

      {/* ── Upgrade prompt — behaviour-triggered for guest users ── */}
      {upgradePrompt && currentUser?.isAnonymous && (
        <UpgradePrompt trigger={upgradePrompt} onDismiss={dismissUpgrade} />
      )}

      {/* ── Receipt scan sheet — global, openable from any screen ── */}
      <ReceiptUploadSheet
        isOpen={receiptSheetOpen}
        onClose={() => setReceiptSheetOpen(false)}
        onNavigate={(page) => { setCurrentPage(page); setActiveTab(page); }}
        user={currentUser}
      />

    </div>
  );
}

export default App;
